import React from "react";
import { createPortal } from "react-dom";
import {
  BellRing,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardPenLine,
  Flag,
  Mail,
  Shield,
  Sparkles,
  Star,
  Trophy,
} from "lucide-react";
import { toast } from "sonner";

import LapstrTextLogo from "@/assets/Lapstr-text.png";
import TeamLogo from "@/components/TeamLogo";
import { useAuth } from "@/contexts/AuthContext";
import { ensurePushSubscribed, getPushPermission } from "@/lib/pushNotifications";
import { PRIVACY_CONSENT_VERSION } from "@/lib/privacyConsent";
import { isNativeApp } from "@/lib/runtime";
import { getConfiguredSeason } from "@/lib/season";
import {
  useAccount,
  useComplianceStatus,
  usePrivacyPreferences,
  useTutorialStatus,
  useUpdateFavoriteTeam,
  useUpdateNativeTutorialStatus,
  useUpdatePrivacyPreferences,
} from "@/queries/account";
import { useStandings } from "@/queries/standings";
import "@/styles/native-onboarding-v3.css";

export const NATIVE_ONBOARDING_VERSION = 1;

const FEATURES = [
  {
    icon: ClipboardPenLine,
    title: "Digitala matchprotokoll",
    text: "Skapa, fyll i och spara kompletta protokoll direkt i appen.",
  },
  {
    icon: Trophy,
    title: "Hela Bauhausligan",
    text: "Matcher, resultat, tabell, slutspel, lag och förare på ett ställe.",
  },
  {
    icon: BellRing,
    title: "När det händer",
    text: "Få matchpåminnelser, laguppställningar och viktig information.",
  },
];

function Progress({ step }) {
  return (
    <div className="native-v3-onboarding-progress" aria-label={`Steg ${step + 1} av 3`}>
      {[0, 1, 2].map((index) => (
        <span key={index} data-active={index === step ? "true" : "false"} />
      ))}
    </div>
  );
}

export default function NativeOnboarding() {
  const { user, ready } = useAuth();
  const { data: compliance, isLoading: complianceLoading } = useComplianceStatus();
  const { data: tutorialData, isLoading: tutorialLoading } = useTutorialStatus();
  const { data: account } = useAccount();
  const { data: privacyPreferences, isLoading: privacyLoading } = usePrivacyPreferences();
  const { data: standings, isLoading: standingsLoading } = useStandings(getConfiguredSeason(), {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const updateTutorial = useUpdateNativeTutorialStatus();
  const updateFavorite = useUpdateFavoriteTeam();
  const updatePrivacy = useUpdatePrivacyPreferences();
  const [step, setStep] = React.useState(0);
  const [pushPermission, setPushPermission] = React.useState("default");
  const [requestingPush, setRequestingPush] = React.useState(false);

  const tutorial = tutorialData?.tutorial;
  const currentVersion = Number(tutorial?.nativeOnboardingVersion || 0);
  const rows = Array.isArray(standings?.rows) ? standings.rows : [];
  const favoriteId = String(account?.favorite_team_id || "");
  const marketingEmailEnabled = privacyPreferences?.marketing_email_enabled === true;
  const marketingEmailAllowed = compliance?.is_adult === true;
  const visible = (
    isNativeApp()
    && ready
    && Boolean(user?.id)
    && !complianceLoading
    && compliance?.complete === true
    && !tutorialLoading
    && currentVersion < NATIVE_ONBOARDING_VERSION
  );

  React.useEffect(() => {
    if (!visible) return undefined;
    let cancelled = false;
    getPushPermission().then((permission) => {
      if (!cancelled) setPushPermission(permission);
    }).catch(() => {});
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      cancelled = true;
      document.body.style.overflow = originalOverflow;
    };
  }, [visible]);

  const selectFavorite = async (teamId) => {
    try {
      await updateFavorite.mutateAsync(teamId);
      toast.success("Favoritlag sparat");
    } catch (error) {
      toast.error(error?.message || "Kunde inte spara favoritlaget");
    }
  };

  const enablePush = async () => {
    if (pushPermission === "denied") {
      toast("Notiser är blockerade", {
        description: "Öppna Androids appinställningar för Lapstr och tillåt notiser.",
      });
      return;
    }
    setRequestingPush(true);
    try {
      await ensurePushSubscribed();
      setPushPermission("granted");
      toast.success("Notiser aktiverade");
    } catch (error) {
      const permission = await getPushPermission().catch(() => "default");
      setPushPermission(permission);
      if (permission !== "denied") {
        toast.error(error?.message || "Kunde inte aktivera notiser");
      }
    } finally {
      setRequestingPush(false);
    }
  };

  const enableMarketingEmail = async () => {
    if (!marketingEmailAllowed || marketingEmailEnabled) return;
    try {
      await updatePrivacy.mutateAsync({
        marketing_email_enabled: true,
        consent_version: PRIVACY_CONSENT_VERSION,
        source: "native_onboarding",
      });
      toast.success("E-postutskick aktiverade");
    } catch (error) {
      toast.error(error?.message || "Kunde inte spara e-postvalet");
    }
  };

  const complete = async () => {
    const highlights = favoriteId ? { favorite_team: true } : undefined;
    try {
      await updateTutorial.mutateAsync({
        nativeOnboardingVersion: NATIVE_ONBOARDING_VERSION,
        ...(highlights ? { nativeHighlights: highlights } : {}),
      });
      window.dispatchEvent(new CustomEvent("lapstr:native-onboarding-completed"));
    } catch (error) {
      toast.error(error?.message || "Kunde inte slutföra introduktionen");
    }
  };

  if (!visible || typeof document === "undefined") return null;

  return createPortal(
    <div className="native-v3-onboarding" role="dialog" aria-modal="true" aria-labelledby="native-onboarding-title">
      <div className="native-v3-onboarding-atmosphere" aria-hidden="true">
        <span />
      </div>

      <main className="native-v3-onboarding-main" key={step} data-step={step}>
        {step === 0 ? (
          <section className="native-v3-onboarding-welcome">
            <div className="native-v3-onboarding-brand">
              <img src="/icons/icon-512x512.png" alt="" className="native-v3-onboarding-brand-mark" />
            </div>
            <div className="native-v3-onboarding-kicker"><Sparkles aria-hidden="true" /> Ett nytt kapitel</div>
            <h1 id="native-onboarding-title">
              Välkommen till
              <span className="native-v3-onboarding-title-brand">
                <em>nya</em>
                <img src={LapstrTextLogo} alt="Lapstr" />
              </span>
            </h1>
            <p>Ny design. Samma fokus på svensk speedway och digitala matchprotokoll.</p>
          </section>
        ) : null}

        {step === 1 ? (
          <section className="native-v3-onboarding-features">
            <div className="native-v3-onboarding-kicker"><Flag aria-hidden="true" /> Allt samlat</div>
            <h1 id="native-onboarding-title">Byggd för<br /><em>speedway</em></h1>
            <p className="native-v3-onboarding-lead">Det viktigaste är närmare, snabbare och tydligare.</p>
            <div className="native-v3-onboarding-feature-list">
              {FEATURES.map(({ icon: Icon, title, text }, index) => (
                <article key={title} style={{ "--feature-index": index }}>
                  <span><Icon aria-hidden="true" /></span>
                  <div><strong>{title}</strong><p>{text}</p></div>
                </article>
              ))}
            </div>
          </section>
        ) : null}

        {step === 2 ? (
          <section className="native-v3-onboarding-personalize">
            <div className="native-v3-onboarding-kicker"><Star aria-hidden="true" /> Gör Lapstr till ditt</div>
            <h1 id="native-onboarding-title">Följ ditt lag</h1>
            <p className="native-v3-onboarding-lead">Välj favoritlag för en snabbare överblick. Du kan ändra valet senare.</p>

            <div className="native-v3-onboarding-team-list" aria-label="Välj favoritlag">
              {standingsLoading ? (
                <p className="native-v3-onboarding-loading">Hämtar lagen...</p>
              ) : rows.map((row) => {
                const teamId = String(row.team_id || "");
                const selected = teamId && teamId === favoriteId;
                return (
                  <button
                    type="button"
                    key={teamId || row.team}
                    data-selected={selected ? "true" : "false"}
                    disabled={!teamId || updateFavorite.isPending}
                    onClick={() => selectFavorite(teamId)}
                  >
                    <TeamLogo teamName={row.team} size={34} fallback={<Shield aria-hidden="true" />} />
                    <span><strong>{row.team}</strong><small>{row.city || "Bauhausligan"}</small></span>
                    {selected ? <Check aria-hidden="true" /> : null}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              className="native-v3-onboarding-push"
              data-granted={pushPermission === "granted" ? "true" : "false"}
              disabled={requestingPush}
              onClick={enablePush}
            >
              <BellRing aria-hidden="true" />
              <span>
                <strong>{pushPermission === "granted" ? "Notiser är aktiverade" : "Aktivera notiser"}</strong>
                <small>Matchpåminnelser, laguppställningar och viktig information.</small>
              </span>
              {pushPermission === "granted" ? <Check aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
            </button>

            {marketingEmailAllowed ? (
              <button
                type="button"
                className="native-v3-onboarding-push native-v3-onboarding-email"
                data-granted={marketingEmailEnabled ? "true" : "false"}
                disabled={privacyLoading || updatePrivacy.isPending || marketingEmailEnabled}
                onClick={enableMarketingEmail}
              >
                <Mail aria-hidden="true" />
                <span>
                  <strong>{marketingEmailEnabled ? "E-post är aktiverad" : "Ta emot e-post från Lapstr"}</strong>
                  <small>Frivilliga nyheter, produktuppdateringar och information.</small>
                </span>
                {marketingEmailEnabled ? <Check aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
              </button>
            ) : null}
          </section>
        ) : null}
      </main>

      <footer className="native-v3-onboarding-footer">
        <Progress step={step} />
        <div>
          {step > 0 ? (
            <button type="button" className="native-v3-onboarding-back" onClick={() => setStep((value) => value - 1)}>
              <ChevronLeft aria-hidden="true" /> Tillbaka
            </button>
          ) : <span />}
          <button
            type="button"
            className="native-v3-onboarding-next"
            disabled={updateTutorial.isPending}
            onClick={() => (step < 2 ? setStep((value) => value + 1) : complete())}
          >
            {step === 0 ? "Upptäck Lapstr" : step === 1 ? "Anpassa" : "Börja använda Lapstr"}
            {step < 2 ? <ChevronRight aria-hidden="true" /> : null}
          </button>
        </div>
      </footer>
    </div>,
    document.body
  );
}
